import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useToast } from '../contexts/ToastContext';
import type { Transcription } from '../utils/supabase';

interface TranscriptionCardProps {
    transcription: Transcription;
    onDelete: (id: string) => void;
}

export function TranscriptionCard({ transcription, onDelete }: TranscriptionCardProps) {
    const [showSummary, setShowSummary] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [summary, setSummary] = useState<string | null>(null);
    const { showToast } = useToast();

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(transcription.text);
            showToast('Copied to clipboard', 'success');
        } catch (error) {
            showToast('Failed to copy to clipboard', 'error');
        }
    };

    const generateSummary = async () => {
        if (summary) {
            setShowSummary(!showSummary);
            return;
        }

        setIsGenerating(true);
        try {
            const response = await fetch('/.netlify/functions/openai', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    tool: 'generate_summary',
                    text: transcription.text
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to generate summary');
            }

            const result = await response.json();
            setSummary(result.content[0].text);
            setShowSummary(true);
        } catch (error) {
            showToast('Failed to generate summary', 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX = e.changedTouches[0].clientX;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        touchEndX = e.changedTouches[0].clientX;
        if (touchStartX - touchEndX > 50) {
            onDelete(transcription.id);
        }
    };

    return (
        <motion.div
            layout
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="card card-hover group"
        >
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <div className="text-sm text-text-secondary font-mono">
                        {format(new Date(transcription.created_at), 'PPp')}
                    </div>
                    {transcription.title && (
                        <h3 className="text-lg font-medium text-text">
                            {transcription.title}
                        </h3>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={copyToClipboard}
                        className="opacity-0 group-hover:opacity-100 text-text-secondary hover:text-text transition-opacity"
                        aria-label="Copy to clipboard"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                    </button>
                    <button
                        onClick={() => onDelete(transcription.id)}
                        className="opacity-0 group-hover:opacity-100 text-error hover:text-error/80 transition-opacity"
                        aria-label="Delete transcription"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="mt-4 text-text">
                {transcription.text}
            </div>

            <div className="mt-4 flex items-center justify-end space-x-2">
                <button
                    onClick={generateSummary}
                    disabled={isGenerating}
                    className="btn btn-secondary text-sm"
                >
                    {isGenerating ? (
                        <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating Summary...
                        </>
                    ) : (
                        <>
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            {showSummary ? 'Hide Summary' : 'Show Summary'}
                        </>
                    )}
                </button>
            </div>

            <AnimatePresence>
                {showSummary && summary && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
                    >
                        <div className="prose dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: summary }} />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
